import { defineStore } from 'pinia'

import {
  destroyGame,
  fetchGameById,
  fetchLocalExport,
  fetchWebExport,
  storeGame,
  updateGame,
} from '@/api/gameApi'

import GameModel from '@/models/game.model.type'
import questionModule from '@/models/question.model'
import { useSwal } from '@/composables'
import { extractClapError } from '@/helpers/errorHelper'
import { useAllGamesStore } from './allGames'
import { useQuestionStore } from '@/store/question/question'
import { reorderVisibleQuestions as reorderVisibleQuestionsInGame } from './reorderVisibleQuestions'

const REMOTES_SUPPORTED_QUESTION_TYPES = new Set([
  'trivia',
  'survey',
  'media',
  'ans_images',
  'discussion',
  'subject',
])

export const isQuestionTypeSupportedByGameType = (
  gameType: string,
  questionType?: string
) => {
  if (!questionType || gameType === 'firebase') {
    return true
  }

  return REMOTES_SUPPORTED_QUESTION_TYPES.has(questionType)
}

type State = {
  isUpdateFromServer: boolean
  isRequiredUpdate: boolean
  isQueListOpen: boolean
  game: Game
  gameToJSON: Game | null
  gameToWeb: Game | null
}

export const useCurrentGameStore = defineStore('currentGame', {
  state: (): State => ({
    isUpdateFromServer: false,
    isRequiredUpdate: false,
    isQueListOpen: false, // only for mobile mode
    game: cloneDeep(new GameModel()),
    gameToJSON: null,
    gameToWeb: null,
  }),
  getters: {
    getGameToJSON: state => state.gameToJSON,
    getGameToWeb: state => state.gameToWeb,
    gameId: state => state.game._id,
    isQuestionTypeSupported(): (questionType?: string) => boolean {
      return (questionType?: string) =>
        isQuestionTypeSupportedByGameType(this.gameType, questionType)
    },
    questions(): Question[] {
      return this.game.questions.filter(question =>
        this.isQuestionTypeSupported(question?.type)
      )
    },
    getQuestionByIndex(): (index: number) => Question {
      return (index: number) => {
        return this.questions[index]
      }
    },
    getQuestionById(): (id: string) => Question | undefined {
      return (id: string): Question | undefined => {
        return this.questions.find(q => q?._id === id)
      }
    },
    questionsLength(): number {
      return this.questions.length
    },
    gameType(): string {
      return this.game.setting.gameType
    },
    isRemotes(): boolean {
      return this.game.setting.gameType === 'remotes'
    },
  },
  actions: {
    // only for mobile mode
    queListToggle() {
      const { isQueSettingOpen } = storeToRefs(useQuestionStore())
      if (isQueSettingOpen.value) {
        useQuestionStore().queSettingToggle()
      }
      this.isQueListOpen = !this.isQueListOpen
    },
    createQuestionToGame(question: Question, index: number) {
      if (index >= 0) {
        this.game.questions.splice(index, 0, question)
      } else {
        this.game.questions.push(question)
      }
    },
    reorderVisibleQuestions(questions: Question[] = []) {
      this.game.questions = reorderVisibleQuestionsInGame(
        this.game.questions,
        questions,
        questionType => this.isQuestionTypeSupported(questionType)
      )
    },
    setGameQuestions(questions: Question[] = []) {
      this.$patch({ game: { questions: cloneDeep(questions) } })
    },
    setGameQuestion(question: Question = new questionModule()) {
      if (!this.questionsLength) {
        this.game.questions.push(cloneDeep(question))
      } else {
        this.game.questions = this.game.questions.map(q => {
          if (q._id === question._id) {
            return cloneDeep(question)
          }
          return q
        })
      }
    },
    // game actions
    setGameByKey<T extends keyof State['game']>(
      key: T,
      value: State['game'][T]
    ) {
      this.isUpdateFromServer = false
      this.game[key] = value
    },
    setGameSettingByKey<T extends keyof State['game']['setting']>(
      key: T,
      value: State['game']['setting'][T]
    ) {
      this.isUpdateFromServer = false
      this.game.setting[key] = value
    },
    // setMedia(key, value) {
    // TODO: add set media with the convention
    // this.game.setting[key] = {
    //   src: 'assets/' + value.url,
    //   url: '',
    //   format: value.format,
    // }
    // },
    setGameSetting(value: State['game']['setting']) {
      this.isUpdateFromServer = false
      Object.assign(this.game.setting, cloneDeep(value))
    },
    clearData() {
      this.$reset()
    },
    storeManyQuestions(questions: Question[]) {
      this.game.questions.push(...questions)
    },

    // fetch from api
    async getGame(id: string) {
      this.game = await fetchGameById(id)
      this.isUpdateFromServer = true
    },
    async createGame(payload: State['game']) {
      const game = await storeGame(payload)
      this.game = { ...this.game, ...game }
      this.isUpdateFromServer = true
    },
    async updateGame(game: State['game']) {
      await updateGame(game)
    },
    async deleteGame(id: string) {
      this.clearData()
      await destroyGame(id)
      useAllGamesStore().destroyGame(id)
    },

    // game to json and web logic
    // actions
    setGameToJSON(payload: any) {
      this.gameToJSON = payload
    },
    unsetGameToJSON() {
      this.gameToJSON = null
    },
    setGameToWeb(payload: any) {
      this.gameToWeb = payload
    },
    unsetGameToWeb() {
      this.gameToWeb = null
    },

    async getLocalExport(id: string) {
      try {
        // await ().checkLogin()
        const game = await fetchLocalExport(id)
        this.setGameToJSON(game)
        return game
      } catch (e) {
        console.error(e)

        useSwal().timeoutSwal({
          icon: 'error',
          title: extractClapError(e as ClapError),
        })
      }
    },

    async getWebExport(id: string) {
      try {
        const game = await fetchWebExport(id)
        this.setGameToWeb(game)
        return game
      } catch (e) {
        console.error(e)

        useSwal().timeoutSwal({
          icon: 'error',
          title: extractClapError(e as ClapError),
        })
      }
    },
  },
})
